/** Ignore width deltas smaller than a typical scrollbar so it cannot thrash scale. */
export const WIDTH_EPSILON_PX = 16;

/**
 * Stay on the current side of the "fits the page" boundary until the host is
 * clearly wider or narrower. A scrollbar is ~15px; without this latch,
 * `PageRender` swaps its DOM (scaled wrapper vs bare page) every toggle and
 * detaches the canvas insert button for the whole click timeout.
 */
export const SCALE_LATCH_PX = 24;

export interface ContainerScaleSnapshot {
  measuredWidth: number;
  latchedBelow: boolean;
}

export function reduceContainerWidth(prev: ContainerScaleSnapshot, nextWidth: number, targetPx: number): ContainerScaleSnapshot {
  if (prev.measuredWidth > 0 && Math.abs(nextWidth - prev.measuredWidth) < WIDTH_EPSILON_PX) {
    return prev;
  }

  const latchedBelow = nextLatch(prev.latchedBelow, nextWidth, targetPx);
  if (nextWidth === prev.measuredWidth && latchedBelow === prev.latchedBelow) {
    return prev;
  }

  return { measuredWidth: nextWidth, latchedBelow };
}

function nextLatch(latchedBelow: boolean, measuredWidth: number, targetPx: number): boolean {
  if (measuredWidth <= 0 || targetPx <= 0) return false;
  if (latchedBelow) return measuredWidth < targetPx + SCALE_LATCH_PX;
  return measuredWidth + SCALE_LATCH_PX < targetPx;
}

export function scaleFromSnapshot(snapshot: ContainerScaleSnapshot, targetPx: number): number {
  const { measuredWidth, latchedBelow } = snapshot;
  if (!latchedBelow || measuredWidth <= 0 || targetPx <= 0) return 1;

  const raw = measuredWidth / targetPx;
  if (raw >= 1) return (targetPx - 0.5) / targetPx;
  return raw;
}
